/* @jsxImportSource @emotion/react */
/* eslint-disable formatjs/no-literal-string-in-jsx */

import { css, keyframes } from '@emotion/react'

import {
  ArchiveIcon,
  ArrowRightIcon,
  AskIcon,
  ChannelIcon,
  ChatIcon,
  CheckmarkIcon,
  CollectionIcon,
  EyeIcon,
  GlobeIcon,
  NoteIcon,
  PlusIcon,
  SearchIcon,
  TableIcon,
} from '@slite/icons'

import handDrawnBorderBlack from '../../../assets/hand-drawn-border-black.svg'

// Same pencil-stroke outline as the secondary button, a bit smaller.
// Warm cream fill so the row reads as a tinted group on the moon-dust bg.
const pillCss = css({
  color: 'var(--shade-00)',
  background: '#ffffff',
  border: '2px solid transparent',
  borderRadius: 9999,
  borderImage: `url("${handDrawnBorderBlack}") 20 / 20px / 0px`,
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: 8,
  padding: '8px 14px 8px 12px',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '16px',
  whiteSpace: 'nowrap',
})

// Each row renders its pills twice; translating one full copy (-50%) loops seamlessly.
const scrollLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
`
const scrollRight = keyframes`
  from {
    transform: translateX(-50%);
  }
  to {
    transform: translateX(0);
  }
`

// Soft fade on both edges so pills drift in/out of view instead of popping.
// `mask-image` clips paint along the row's horizontal axis; the marquee itself
// keeps animating across the full width.
const containerCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Clip horizontally only so the pill outlines / strokes aren't cropped
     at the top/bottom of the row. overflow-x: clip + overflow-y: visible
     keeps the marquee constrained to its row width without forcing a
     vertical clip. */
  overflow-x: clip;
  overflow-y: visible;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    #000 15%,
    #000 85%,
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    #000 15%,
    #000 85%,
    transparent 100%
  );
`

const trackCss = css`
  display: flex;
  width: max-content;
`
// padding-right matches the gap so the two copies tile evenly across the -50% seam.
const groupCss = css`
  display: flex;
  gap: 6px;
  padding-right: 6px;
`

const ROW_COUNT = 3

type McpTool = {
  // exact MCP tool name (see api/src/services/mcp/{slite,super}/tools)
  name: string
  label: string
  icon: React.ReactNode
}

const tools: McpTool[] = [
  {
    name: 'query-super-sources',
    label: 'Search every source',
    icon: <GlobeIcon size={16} />,
  },
  {
    name: 'ask-slite',
    label: 'Ask your knowledge base',
    icon: <AskIcon size={16} />,
  },
  {
    name: 'search-notes',
    label: 'Search docs',
    icon: <SearchIcon size={16} />,
  },
  {
    name: 'get-note',
    label: 'Read a doc',
    icon: <NoteIcon size={16} />,
  },
  {
    name: 'create-note',
    label: 'Create a doc',
    icon: <PlusIcon size={16} />,
  },
  {
    name: 'update-note',
    label: 'Update a doc',
    icon: <NoteIcon size={16} />,
  },
  {
    name: 'append-blocks',
    label: 'Append content',
    icon: <PlusIcon size={16} />,
  },
  {
    name: 'modify-block',
    label: 'Edit a block',
    icon: <NoteIcon size={16} />,
  },
  {
    name: 'modify-range',
    label: 'Edit a range',
    icon: <NoteIcon size={16} />,
  },
  {
    name: 'remove-blocks',
    label: 'Remove blocks',
    icon: <ArchiveIcon size={16} />,
  },
  {
    name: 'verify-note',
    label: 'Verify a doc',
    icon: <CheckmarkIcon size={16} />,
  },
  {
    name: 'set-note-review-state',
    label: 'Set review state',
    icon: <EyeIcon size={16} />,
  },
  {
    name: 'move-note',
    label: 'Move a doc',
    icon: <ArrowRightIcon size={16} />,
  },
  {
    name: 'archive-note',
    label: 'Archive a doc',
    icon: <ArchiveIcon size={16} />,
  },
  {
    name: 'restore-note',
    label: 'Restore a doc',
    icon: <ArchiveIcon size={16} />,
  },
  {
    name: 'get-note-children',
    label: 'List child docs',
    icon: <CollectionIcon size={16} />,
  },
  {
    name: 'create-collection',
    label: 'Create a collection',
    icon: <CollectionIcon size={16} />,
  },
  {
    name: 'update-collection',
    label: 'Update a collection',
    icon: <TableIcon size={16} />,
  },
  {
    name: 'list-channels',
    label: 'List channels',
    icon: <ChannelIcon size={16} />,
  },
  {
    name: 'create-channel',
    label: 'Create a channel',
    icon: <ChannelIcon size={16} />,
  },
  {
    name: 'update-channel',
    label: 'Update a channel',
    icon: <ChannelIcon size={16} />,
  },
  {
    name: 'list-comment-threads',
    label: 'List comments',
    icon: <ChatIcon size={16} />,
  },
  {
    name: 'create-comment-thread',
    label: 'Add a comment',
    icon: <ChatIcon size={16} />,
  },
  {
    name: 'reply-to-comment-thread',
    label: 'Reply to a comment',
    icon: <ChatIcon size={16} />,
  },
  {
    name: 'resolve-comment-thread',
    label: 'Resolve a comment',
    icon: <CheckmarkIcon size={16} />,
  },
]

const rows = Array.from({ length: ROW_COUNT }, (_, r) =>
  tools.filter((_, i) => i % ROW_COUNT === r)
)

export const McpTools = () => {
  return (
    <div css={containerCss}>
      {rows.map((rowTools, r) => (
        <div key={r}>
          <div
            css={[
              trackCss,
              css`
                animation: ${r % 2 === 0 ? scrollLeft : scrollRight} 200s linear
                  infinite;
              `,
            ]}
          >
            {[0, 1].map((copy) => (
              <div key={copy} css={groupCss} aria-hidden={copy === 1}>
                {rowTools.map((tool) => (
                  <span key={tool.name} css={pillCss}>
                    {tool.icon}
                    {tool.label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
